import type { MultiStepFormStep } from '@components/Form/context'
import { isValid } from 'date-fns'
import type { FieldValues } from 'react-hook-form'
import type { NameModel } from '@/types/Common/NameModel'
import type { NameInfo } from '@/types/Company'

/**
 * Converts a name object or string to a formatted full name string.
 */
export function formatFullName(
  name:
    | NameInfo
    | {
        firstName?: string | null
        lastName?: string | null
      }
    | string
    | null
    | undefined,
): string {
  if (!name) {
    return ''
  }
  if (typeof name === 'string') {
    return name.trim()
  }
  const firstName = name.firstName ?? ''
  const lastName = name.lastName ?? ''
  return `${firstName} ${lastName}`.trim()
}

export function capitalizeFirstLetter(str: string | undefined): string {
  if (!str) {
    return ''
  }
  return str.charAt(0).toUpperCase() + str.slice(1)
}

export function lowercaseFirstLetter(str: string | undefined): string {
  if (!str) {
    return ''
  }
  return str.charAt(0).toLowerCase() + str.slice(1)
}

export function formatDateToReadableString(isoDate: string): string {
  const date = new Date(isoDate)
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }
  return date.toLocaleDateString('no-NO', options)
}

export function formatDateWithSlashes(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export const getExpectedDigitCount = (pattern: string): number => {
  return (pattern.match(/X/g) ?? []).length
}

export const normalizeSpaces = (value: string): string => {
  return value.replace(/\s+/g, ' ').trim()
}

export const unformatValue = (value: string): string => {
  return value.replace(/\D/g, '')
}

/**
 * Converts a string to a Date object if it is a valid date.
 * @param value - The value to convert.
 * @returns The Date object if the value is a valid date, otherwise undefined.
 */
export function normalizeToDate(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return isValid(value) ? value : undefined
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const date = new Date(value)
    return isValid(date) ? date : undefined
  }
  return undefined
}

/* eslint-disable */
export const noop = <T extends readonly unknown[]>(...args: T): void => {
  // use args, but don't do anything with them
  args
}
/* eslint-enable */

/**
 * Get the values of an enum as a tuple
 * @param obj - The enum to get the values of
 * @returns The values of the enum as a tuple
 */
export function getEnumValues<T extends Record<string, string>>(
  obj: T,
): [T[keyof T], ...T[keyof T][]] {
  return Object.values(obj) as [T[keyof T], ...T[keyof T][]]
}

export const getFieldName = (fieldName: string, validationFieldNamePrefix?: string) => {
  return validationFieldNamePrefix ? `${validationFieldNamePrefix}.${fieldName}` : fieldName
}

/**
 * Creates a type-safe step definition function for multi-step forms
 * @returns A function that accepts an array of step definitions
 */
export const defineSteps = <T extends FieldValues>() => {
  return (steps: (MultiStepFormStep<T> | MultiStepFormStep<T>[])[]) => steps
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`
}

export const formatDate = (
  date: Date,
  options?: {
    day?: 'numeric' | '2-digit'
    month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow'
    year?: 'numeric' | '2-digit'
  },
): string => {
  return date.toLocaleDateString('no-NO', {
    day: options?.day ?? '2-digit',
    month: options?.month ?? '2-digit',
    year: options?.year ?? 'numeric',
  })
}
export const formatTime = (time: string) => {
  return new Date(time).toLocaleTimeString('nb-NO', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const formatOrgNumber = (value: string): string => {
  if (value.startsWith('NO')) return value
  const digits = value.replace(/\D/g, '')
  const groups = digits.match(/.{1,3}/g)
  return groups ? groups.join(' ') : digits
}

export const formatOrganizationAddress = (
  address: { street: string; zipCode: string; city: string } | undefined,
): string => {
  if (!address?.street || !address.zipCode || !address.city) {
    return 'Adresse ikke tilgjengelig'
  }
  return `${address.street}, ${address.zipCode} ${address.city}`
}

export type ParsedValue = string | Record<string, unknown> | unknown[] | null

export const isRecord = (value: ParsedValue): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

type DifferenceFormatter = (key: string, oldValue: string, newValue: string) => string

export const getObjectDifferences = (
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
  formatter: DifferenceFormatter,
): string[] => {
  const differences: string[] = []
  const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)])

  for (const key of allKeys) {
    if (oldObj[key] !== newObj[key]) {
      if (key === 'isEmpty') continue

      const oldVal = oldObj[key]?.toString() ?? ''
      const newVal = newObj[key]?.toString() ?? ''

      if (oldVal !== newVal) {
        differences.push(formatter(key, oldVal, newVal))
      }
    }
  }

  return differences
}

export const getArrayDifferences = (oldArray: unknown[], newArray: unknown[]): string[] => {
  const differences: string[] = []

  if (oldArray.length !== newArray.length) {
    differences.push(`length: ${oldArray.length} → ${newArray.length}`)
  }

  const maxLength = Math.max(oldArray.length, newArray.length)
  for (let i = 0; i < maxLength; i++) {
    const oldItem = oldArray[i]
    const newItem = newArray[i]

    if (JSON.stringify(oldItem) !== JSON.stringify(newItem)) {
      if (oldItem && newItem && typeof oldItem === 'object' && typeof newItem === 'object') {
        const oldObj = oldItem as Record<string, unknown>
        const newObj = newItem as Record<string, unknown>

        for (const key of Object.keys({ ...oldObj, ...newObj })) {
          if (oldObj[key] !== newObj[key]) {
            const oldVal = oldObj[key]?.toString() ?? ''
            const newVal = newObj[key]?.toString() ?? ''
            if (oldVal !== newVal) {
              differences.push(`${key}: "${oldVal}" → "${newVal}"`)
            }
          }
        }
      } else {
        differences.push(`item ${i}: ${JSON.stringify(oldItem)} → ${JSON.stringify(newItem)}`)
      }
    }
  }

  return differences
}

export const toStringSafe = (value: string | number): string =>
  typeof value === 'string' ? value : value.toString()

export const invertNegativeNumber = (value: number): number => (value >= 0 ? value : value * -1)

/**
 * Converts a positive amount into its non-positive (0 or negative) counterpart.
 * The backend stores share/extra capital as a debt: 0 or negative. UI fields display
 * these as positive amounts, so this is the inverse of `invertNegativeNumber` and is
 * used when mapping form values back into API request payloads.
 */
export const toNonPositiveNumber = (value: number): number => (value > 0 ? value * -1 : value)

/**
 * Rounds a number to 2 decimal places.
 * Useful for currency comparisons to avoid floating point precision issues.
 */
export const roundTo2Decimals = (n: number): number => Math.round(n * 100) / 100

/**
 * Splits a full name into firstName and lastName.
 * - Single word names → treated as lastName, firstName = null
 * - Multiple words → last word as lastName, rest as firstName
 * - Empty/null names → returns null
 * - If input is already a NameInfo object, returns it directly
 */
export function splitName(fullName: NameInfo | string | null | undefined): NameInfo | null {
  if (!fullName) {
    return null
  }

  if (typeof fullName === 'object' && 'firstName' in fullName && 'lastName' in fullName) {
    return fullName
  }

  if (typeof fullName !== 'string') {
    return null
  }

  if (!fullName.trim()) {
    return null
  }

  const trimmedName = fullName.trim()
  const parts = trimmedName.split(/\s+/).filter((part) => part.length > 0)

  if (parts.length === 0) {
    return null
  }

  if (parts.length === 1) {
    return {
      firstName: null,
      lastName: parts[0] || null,
    }
  }

  const lastName = parts[parts.length - 1] || null
  const firstName = parts.slice(0, parts.length - 1).join(' ') || null

  return {
    firstName,
    lastName,
  }
}

/**
 * Parses a full name string into a Name object.
 * - null/undefined input → returns null/undefined respectively
 * - Empty / whitespace-only string → returns null
 * - Single word → { firstName: word, lastName: null }
 * - Multiple words → last word as lastName, rest as firstName
 */
export function parseToName(fullName: string | null | undefined): NameModel | null | undefined {
  if (fullName === undefined) return undefined
  if (fullName === null) return null

  const trimmed = fullName.trim()
  if (!trimmed) return null

  const parts = trimmed.split(/\s+/)

  if (parts.length === 1) {
    return { firstName: parts[0] as string }
  }

  return {
    firstName: parts.slice(0, parts.length - 1).join(' '),
    lastName: parts[parts.length - 1],
  }
}

export const isUuid = (value: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)
}

export const isBrowser = typeof window !== 'undefined'

/**
 * Checks whether all bits in `flag` are set in `value`.
 * hasFlag(0b0110, 0b0100) → true
 * hasFlag(0b0110, 0b1100) → false  (0b1000 is not set)
 */
export const hasFlag = (value: number, flag: number): boolean => (value & flag) === flag
